/**
 * Spaced review (SRS, Duolingo-style). The review queue is DERIVED from
 * already-synced progress (`buildReviewQueue`): no new state. Reviewing
 * a lesson means opening its course and playing it again — the existing write-path
 * pushes the next review. Only shows up when there are overdue lessons.
 */
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { RotateCcw, Sparkles } from "@fleet/icons";
import { buildReviewQueue } from "@space/shared";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { useReviewProgress } from "../../hooks/use-course-data";

/** Maximum overdue review cards shown (the most behind). */
const MAX_DUE_SHOWN = 4;

export const ReviewSection: Component = () => {
  const { t } = useI18n();
  const { query } = useReviewProgress();

  const queue = createMemo(() => buildReviewQueue(query.data ?? [], Date.now()));
  const titleByLesson = createMemo(() => {
    const map = new Map<string, string>();
    for (const r of query.data ?? []) {
      if (r.lesson_id) {
        map.set(r.lesson_id, r.lesson_title ?? t(msg`Lesson`));
      }
    }
    return map;
  });

  return (
    <Show when={queue().due.length > 0}>
      <section
        class="rounded-3xl border border-joy-flame/30 bg-joy-flame/5 p-5 md:p-6"
        data-testid="review-section"
      >
        <div class="flex items-center gap-2">
          <span class="flex h-9 w-9 items-center justify-center rounded-2xl bg-joy-flame/15 text-joy-flame">
            <RotateCcw class="h-5 w-5" />
          </span>
          <div>
            <h2 class="font-display text-lg font-bold">{t(msg`Time to review`)}</h2>
            <p class="text-xs text-muted-foreground">
              {t(msg`Reinforce what you learned so you don't lose your memory streak.`)}
            </p>
          </div>
          <span
            class="ml-auto flex items-center gap-1 rounded-full bg-joy-flame/15 px-2.5 py-1 text-xs font-bold text-joy-flame"
            data-testid="review-due-count"
          >
            <Sparkles class="h-3.5 w-3.5" /> {queue().due.length}
          </span>
        </div>

        <div class="mt-4 grid gap-2.5">
          <For each={queue().due.slice(0, MAX_DUE_SHOWN)}>
            {(item) => (
              <Link
                to="/learn/$courseId"
                params={{ courseId: item.courseId }}
                class="group flex items-center gap-3 rounded-2xl border border-border bg-surface p-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-joy-flame/50 hover:shadow-md"
                data-testid="review-item"
              >
                <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-joy-flame/12 text-joy-flame">
                  <RotateCcw class="h-4 w-4" />
                </span>
                <span class="min-w-0 flex-1 truncate font-semibold">
                  {titleByLesson().get(item.lessonId) ?? t(msg`Lesson`)}
                </span>
                <span class="text-joy-flame opacity-0 transition-opacity group-hover:opacity-100">
                  {t(msg`Review`)} →
                </span>
              </Link>
            )}
          </For>
        </div>
      </section>
    </Show>
  );
};
